import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  HelpCircle, CheckCircle, ArrowUpRight, ArrowDownRight, Tags
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const commonCategories = [
  'Fuel',
  'Toll Fees',
  'Maintenance',
  'Insurance',
  'Salary',
  'Office',
  'Vehicle',
  'Income',
  'Other'
];

export default function BankingUncategorized() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorizing, setCategorizing] = useState({});

  useEffect(() => {
    fetchUncategorized();
  }, []);

  const fetchUncategorized = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/banking/uncategorized`);
      setTransactions(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load transactions'));
    } finally {
      setLoading(false);
    }
  };

  const handleCategorize = async (txnId, category) => {
    setCategorizing(prev => ({ ...prev, [txnId]: true }));
    try {
      await axios.put(`${API_URL}/banking/transactions/${txnId}/categorize?category=${encodeURIComponent(category)}`);
      toast.success('Transaction categorized');
      // Remove from list
      setTransactions(prev => prev.filter(t => t.id !== txnId));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to categorize'));
    } finally {
      setCategorizing(prev => ({ ...prev, [txnId]: false }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banking-uncategorized">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Uncategorized Queue</h1>
            <p className="text-muted-foreground mt-1">Review and categorize transactions that need attention</p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            {transactions.length} pending
          </Badge>
        </div>

        {/* Tip */}
        {transactions.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Tags className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Pro Tip</p>
                  <p className="text-sm text-amber-700">
                    If you see similar transactions, create a <a href="/admin/banking/rules" className="underline">category rule</a> to 
                    auto-categorize them in future imports.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Transactions to Review</CardTitle>
            <CardDescription>Select a category for each transaction</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <h3 className="text-lg font-medium mb-2">All caught up!</h3>
                <p className="text-muted-foreground">No uncategorized transactions</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Assign Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {txn.date}
                      </TableCell>
                      <TableCell className="max-w-sm">
                        <p className="truncate" title={txn.description}>{txn.description}</p>
                        {txn.reference && (
                          <p className="text-xs text-muted-foreground">Ref: {txn.reference}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {txn.transaction_type === 'credit' ? (
                          <Badge className="bg-green-100 text-green-700">
                            <ArrowUpRight className="h-3 w-3 mr-1" />
                            Credit
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <ArrowDownRight className="h-3 w-3 mr-1" />
                            Debit
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${
                        txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(txn.amount)}
                      </TableCell>
                      <TableCell>
                        <Select
                          disabled={categorizing[txn.id]}
                          onValueChange={(value) => handleCategorize(txn.id, value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {commonCategories.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
